import axios from 'axios';
import {
  ProjectFileBatchAdminRow,
  ProjectFileBatchSnapshot,
} from '@shared/models/project-file-batch.model';
import {
  getProjectFileDrawingImagePath,
  getProjectFilePageRasterPath,
} from '@shared/helpers/project-file-drawing-image-path.helpers';
import type { FfuBaselineChange } from '@shared/helpers/ffu-baseline.helpers';
import type { ProjectFilePipeId } from '@shared/models/project-file-pipe.model';
import type { ProjectFileClientUpload } from '../../helpers/project-file-upload-normalize.helpers';

export const adminProjectFileBatchesApi = {
  list: () =>
    axios
      .get<ProjectFileBatchAdminRow[]>('/admin/project-file-batches')
      .then((r) => r.data),

  get: (id: string) =>
    axios
      .get<ProjectFileBatchSnapshot>(`/admin/project-file-batches/${id}`)
      .then((r) => r.data),

  // Test-only entry point. Posts to the regular creator route so the admin
  // user creates a batch under their own auth, exactly the same path
  // production callers will use. Will be removed once phase 7 lands real
  // entry-point UIs (create-project, project drop-zone, AI chat attach).
  create: (
    uploads: ProjectFileClientUpload[],
    options?: {
      projectId?: number;
      pipeId?: ProjectFilePipeId;
      model?: string | null;
    },
  ) => {
    const form = new FormData();
    for (const upload of uploads) {
      form.append('files', upload.file, upload.relativePath);
    }
    if (options?.projectId !== undefined) {
      form.append('projectId', String(options.projectId));
    }
    if (options?.pipeId) form.append('pipeId', options.pipeId);
    if (options?.model) form.append('model', options.model);
    return axios
      .post<ProjectFileBatchSnapshot>('/project-file-batches', form)
      .then((r) => r.data);
  },

  /** FFU baseline POC: a project from the template and the batch's final output. */
  createProject: (id: string) =>
    axios
      .post<{ projectId: number; changes: FfuBaselineChange[] }>(
        `/admin/project-file-batches/${id}/project`,
      )
      .then((r) => r.data),

  getDrawing: (batchId: string, fileId: string, drawingId: string) =>
    axios
      .get<Blob>(
        `/${getProjectFileDrawingImagePath(batchId, fileId, drawingId)}`,
        { responseType: 'blob' },
      )
      .then((r) => r.data),

  getPageRaster: (batchId: string, fileId: string, pageIndex: number) =>
    axios
      .get<Blob>(
        `/${getProjectFilePageRasterPath(batchId, fileId, pageIndex)}`,
        { responseType: 'blob' },
      )
      .then((r) => r.data),
};
